import { Suspense, useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

import Root from '@/pages/Root';
import LngRoot from '@/pages/LngRoot';
import Redirect from '@/components/molecules/Redirect';
import RouteErrorBoundary from '@/RouteErrorBoundary';
import Loading from '@/components/molecules/Loading';
import { onboardingRoutes } from '@/routes/onboarding.routes';
import { memberRoutes } from '@/routes/member.routes';
import { supportRoutes } from '@/routes/support.routes';
import { importOrReload } from '@/utils/route.utils';
import useTranslation from '@/hooks/useTranslation';
import { UpdateRequired } from '@/components/templates/common/UpdateRequired/UpdateRequired';
import {
  clearPendingNavigationDestination,
  initializeBuildRecovery,
  isBuildRecoveryError,
  setPendingNavigationDestination,
} from '@/utils/buildRecovery';
import { initializeOnboardingDashboardTransition } from '@/utils/onboardingTransition';

initializeBuildRecovery();
initializeOnboardingDashboardTransition();

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Root />}>
      <Route
        path=":lng"
        element={<LngRoot />}
        errorElement={<RouteErrorBoundary />}
      >
        {supportRoutes}
        {onboardingRoutes}
        {memberRoutes}

        <Route
          path="login"
          lazy={importOrReload(() => import('@/pages/login/Login'))}
        />
        <Route
          path="logout"
          lazy={importOrReload(() => import('@/pages/logout/Logout'))}
        />
        <Route
          path="finish-logout"
          lazy={importOrReload(() => import('@/pages/logout/Finish-logout'))}
        />
        <Route
          path="privacy-policy"
          lazy={importOrReload(() => import('@/pages/privacy-policy/page'))}
        />
        <Route
          path="terms-of-service"
          lazy={importOrReload(() => import('@/pages/terms-of-service/page'))}
        />
        <Route
          path="notice-of-privacy-under-hipaa"
          lazy={importOrReload(
            () => import('@/pages/notice-of-privacy-under-hipaa/page')
          )}
        />
        <Route
          path="finish-login"
          lazy={importOrReload(
            () => import('@/pages/login/ClientLandingPageLogin')
          )}
        />

        <Route index element={<Redirect path="/login" />} />

        <Route
          path="*"
          lazy={importOrReload(() => import('@/pages/not-found'))}
        />
      </Route>
    </Route>
  )
);

export function App() {
  const { reset } = useQueryErrorResetBoundary();
  const t = useTranslation('common');

  useEffect(
    () =>
      router.subscribe((routerState) => {
        const pendingLocation = routerState.navigation.location;
        if (pendingLocation) {
          setPendingNavigationDestination(pendingLocation);
        } else if (routerState.errors === null) {
          clearPendingNavigationDestination();
        }
      }),
    []
  );

  return (
    <ErrorBoundary
      onReset={reset}
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (isBuildRecoveryError(error)) {
          return <UpdateRequired />;
        }

        return (
          <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
            <h1 className="text-2xl font-bold">{t('errors.fatal.title')}</h1>
            <p className="mt-2 text-slate-600">{t('errors.fatal.subtitle')}</p>
            <button
              onClick={() => resetErrorBoundary()}
              className="mt-4 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              {t('errors.try-again')}
            </button>
          </div>
        );
      }}
    >
      <Suspense fallback={<Loading fullscreen />}>
        <RouterProvider router={router} />
      </Suspense>
    </ErrorBoundary>
  );
}
